import { Paperclip, Plus, RotateCcw, Trash2, Upload } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useProjectCostCodes } from "@/hooks/use-budgets";
import type { CreateChangeOrderPayload, PcoLinkagePayload } from "@/hooks/use-change-orders";
import { useCommittedSubsForCostCode, useCreateChangeOrder } from "@/hooks/use-change-orders";
import type { Commitment } from "@/hooks/use-commitments";
import { useCommitment, useCommitments } from "@/hooks/use-commitments";
import { formatCurrency } from "@/lib/formatters";
import { cn } from "@/lib/utils";

const MAX_ATTACHMENT_SIZE = 50 * 1024 * 1024; // 50 MB — must match server
const ALLOWED_ATTACHMENT_MIME = new Set([
  "application/pdf",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/tiff",
  "image/heic",
]);

function validateAttachment(file: File): string | null {
  if (file.size > MAX_ATTACHMENT_SIZE) {
    return `"${file.name}" exceeds the 50 MB limit`;
  }
  const baseMime = (file.type.split(";")[0] ?? file.type).trim();
  if (!ALLOWED_ATTACHMENT_MIME.has(baseMime) && !baseMime.startsWith("image/")) {
    return `"${file.name}" has an unsupported type. Accepted: PDF, Excel, Word, images`;
  }
  return null;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface LineRow {
  key: string;
  description: string;
  scheduledValue: string;
  costCodeId: string;
}

let lineKeyCounter = 0;
function blankLine(defaultCostCodeId = ""): LineRow {
  lineKeyCounter += 1;
  return {
    key: `line-${Date.now()}-${lineKeyCounter}`,
    description: "",
    scheduledValue: "",
    costCodeId: defaultCostCodeId,
  };
}

type Step = 1 | 2 | 3 | 4;

const STEPS: { id: Step; label: string }[] = [
  { id: 1, label: "1. Basics" },
  { id: 2, label: "2. Line Items" },
  { id: 3, label: "3. Schedule" },
  { id: 4, label: "4. Supporting Documents" },
];

export function NewPcoDialog({
  projectId,
  open,
  onOpenChange,
}: {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const create = useCreateChangeOrder();
  const { data: commitments } = useCommitments({ projectId });
  const { data: costCodes } = useProjectCostCodes(projectId);

  const [step, setStep] = useState<Step>(1);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  // Linkage type — "Existing Commitment" attaches to a parent commitment;
  // "Cost Code" is the unforeseen-condition flow that allocates against a
  // cost code on the project budget (US-125 / WI-037a).
  const [linkageType, setLinkageType] = useState<"commitment" | "cost_code">("commitment");
  const [parentCommitmentId, setParentCommitmentId] = useState<string>("");
  const [costCodeId, setCostCodeId] = useState<string>("");
  // The sub-picker uses this string. Special sentinel "__new_vendor__" means
  // "no committed sub will perform this work, requires buyout downstream."
  const [selectedSub, setSelectedSub] = useState<string>("");
  const [scheduleImpactDays, setScheduleImpactDays] = useState<string>("");
  const [clientDecisionDeadline, setClientDecisionDeadline] = useState<string>("");
  const [retentionOverride, setRetentionOverride] = useState<string>("");
  const [lines, setLines] = useState<LineRow[]>([blankLine()]);
  const [linesHydratedFor, setLinesHydratedFor] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingFiles, setPendingFiles] = useState<{ key: string; file: File }[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingFileKeyRef = useRef(0);

  const parentCandidates: Commitment[] = (commitments ?? []).filter(
    (c) => c.commitmentType !== "change_order" && c.status !== "terminated",
  );

  const selectedParent = useMemo(
    () => parentCandidates.find((c) => c.id === parentCommitmentId) ?? null,
    [parentCandidates, parentCommitmentId],
  );

  // Subs already committed under the chosen cost code on this project.
  // Loaded only when the user has chosen the cost-code linkage flow.
  const committedSubsQuery = useCommittedSubsForCostCode(
    linkageType === "cost_code" ? projectId : null,
    linkageType === "cost_code" ? costCodeId || null : null,
  );
  const committedSubs = committedSubsQuery.data ?? [];
  const isNewVendor = selectedSub === "__new_vendor__";
  const selectedCommittedSub = isNewVendor
    ? null
    : (committedSubs.find((s) => s.companyId === selectedSub) ?? null);

  const selectedCostCode = useMemo(
    () => (costCodes ?? []).find((cc) => cc.id === costCodeId) ?? null,
    [costCodes, costCodeId],
  );

  // Resolved subcontractor display name across both linkage flows.
  const subcontractorName: string | null =
    linkageType === "commitment"
      ? (selectedParent?.companyName ?? null)
      : isNewVendor
        ? "New Vendor — buyout pending"
        : (selectedCommittedSub?.companyName ?? null);

  // Fetch parent commitment detail (for line items) only when a parent is selected
  // and the user has advanced past step 1.
  const parentDetailQuery = useCommitment(parentCommitmentId);
  const parentLineItems = parentDetailQuery.data?.lineItems;

  // Hydrate PCO line items from parent when the parent changes (description +
  // costCode copied, scheduledValue blank so the user enters the delta).
  useEffect(() => {
    if (!parentCommitmentId) {
      if (linesHydratedFor !== null) {
        setLines([blankLine()]);
        setLinesHydratedFor(null);
      }
      return;
    }
    if (linesHydratedFor === parentCommitmentId) return;
    if (!parentLineItems) return;
    if (parentLineItems.length === 0) {
      setLines([blankLine()]);
    } else {
      setLines(
        parentLineItems.map((li) => {
          lineKeyCounter += 1;
          return {
            key: `line-${Date.now()}-${lineKeyCounter}`,
            description: li.description ?? "",
            scheduledValue: "",
            costCodeId: li.costCodeId ?? "",
          };
        }),
      );
    }
    setLinesHydratedFor(parentCommitmentId);
  }, [parentCommitmentId, parentLineItems, linesHydratedFor]);

  const total = lines.reduce((s, l) => s + (Number(l.scheduledValue) || 0), 0);
  const direction = total > 0 ? "Additive" : total < 0 ? "Deductive" : "Zero";

  function resetForm() {
    setStep(1);
    setTitle("");
    setDescription("");
    setLinkageType("commitment");
    setParentCommitmentId("");
    setCostCodeId("");
    setSelectedSub("");
    setScheduleImpactDays("");
    setClientDecisionDeadline("");
    setRetentionOverride("");
    setLines([blankLine()]);
    setLinesHydratedFor(null);
    setFormError(null);
    setPendingFiles([]);
    setIsUploading(false);
  }

  function handleFilesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    const accepted: { key: string; file: File }[] = [];
    for (const f of files) {
      const err = validateAttachment(f);
      if (err) {
        toast.error(err);
        continue;
      }
      pendingFileKeyRef.current += 1;
      accepted.push({ key: `pf-${pendingFileKeyRef.current}`, file: f });
    }
    if (accepted.length > 0) {
      setPendingFiles((prev) => [...prev, ...accepted]);
    }
  }

  function removePendingFile(key: string) {
    setPendingFiles((prev) => prev.filter((entry) => entry.key !== key));
  }

  function updateLine(i: number, patch: Partial<LineRow>) {
    setLines((prev) => prev.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  }

  function addLine() {
    // Cost-code-linked PCOs default new lines to the header cost code so
    // the user doesn't have to pick the same code over and over.
    const defaultCc = linkageType === "cost_code" ? costCodeId : "";
    setLines((prev) => [...prev, blankLine(defaultCc)]);
  }

  /**
   * Set the header cost code (cost_code linkage flow) and backfill any
   * existing line that still has a blank cost code — the user almost
   * always wants the lines to default to the cost code they just picked.
   * Lines the user has already customized are left alone.
   */
  function selectHeaderCostCode(next: string) {
    setCostCodeId(next);
    setSelectedSub("");
    if (next) {
      setLines((prev) => prev.map((l) => (l.costCodeId ? l : { ...l, costCodeId: next })));
    }
  }

  function removeLine(i: number) {
    setLines((prev) => (prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i)));
  }

  function resetLinesFromParent() {
    if (!parentLineItems) return;
    if (parentLineItems.length === 0) {
      setLines([blankLine()]);
    } else {
      setLines(
        parentLineItems.map((li) => {
          lineKeyCounter += 1;
          return {
            key: `line-${Date.now()}-${lineKeyCounter}`,
            description: li.description ?? "",
            scheduledValue: "",
            costCodeId: li.costCodeId ?? "",
          };
        }),
      );
    }
  }

  function validateStep1(): string | null {
    if (!title.trim()) return "Title is required";
    if (linkageType === "commitment") {
      if (!parentCommitmentId) {
        return "Linked Commitment is required (or switch to 'Cost Code' for unforeseen conditions)";
      }
    } else {
      if (!costCodeId) return "Cost Code is required";
      if (!selectedSub) {
        return "Pick a subcontractor with an existing commitment, or 'New Vendor (requires buyout)'";
      }
    }
    return null;
  }

  function buildLinkagePayload(): PcoLinkagePayload {
    if (linkageType === "commitment") {
      return { linkageType: "commitment", parentCommitmentId };
    }
    return {
      linkageType: "cost_code",
      costCodeId,
      selectedCompanyId: isNewVendor ? null : selectedSub,
      newVendor: isNewVendor,
    };
  }

  function validateStep2(): string | null {
    const cleanLines = lines
      .map((l) => ({
        description: l.description.trim(),
        scheduledValue: l.scheduledValue.trim(),
      }))
      .filter((l) => l.description && l.scheduledValue);
    if (cleanLines.length === 0) {
      return "At least one line item with description and amount is required";
    }
    const sum = cleanLines.reduce((s, l) => s + Number(l.scheduledValue), 0);
    if (sum === 0) return "Total cannot be zero";
    if (sum < 0 && !cleanLines.some((l) => Number(l.scheduledValue) < 0)) {
      return "Deductive change orders require at least one negative line item";
    }
    return null;
  }

  function goNext() {
    setFormError(null);
    if (step === 1) {
      const err = validateStep1();
      if (err) {
        setFormError(err);
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      const err = validateStep2();
      if (err) {
        setFormError(err);
        return;
      }
      setStep(3);
      return;
    }
    if (step === 3) {
      setStep(4);
    }
  }

  function goBack() {
    setFormError(null);
    if (step === 4) setStep(3);
    else if (step === 3) setStep(2);
    else if (step === 2) setStep(1);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    const step1Err = validateStep1();
    if (step1Err) {
      setFormError(step1Err);
      setStep(1);
      return;
    }
    const step2Err = validateStep2();
    if (step2Err) {
      setFormError(step2Err);
      setStep(2);
      return;
    }

    const cleanLines = lines
      .map((l) => ({
        description: l.description.trim(),
        scheduledValue: l.scheduledValue.trim(),
        costCodeId: l.costCodeId || null,
      }))
      .filter((l) => l.description && l.scheduledValue);

    const payload: CreateChangeOrderPayload = {
      projectId,
      title: title.trim(),
      description: description.trim() || undefined,
      linkage: buildLinkagePayload(),
      scheduleImpactDays: scheduleImpactDays ? Number(scheduleImpactDays) : null,
      clientDecisionDeadline: clientDecisionDeadline || null,
      retentionPercentOverride: retentionOverride.trim() || null,
      lineItems: cleanLines.map((l, idx) => ({
        itemNo: String(idx + 1),
        description: l.description,
        // Cost-code-linked PCOs default per-line cost code to the header
        // cost code so the SOV rollup ties back to the header allocation.
        scheduledValue: l.scheduledValue,
        costCodeId: l.costCodeId || (linkageType === "cost_code" ? costCodeId : null),
      })),
    };

    create.mutate(payload, {
      onSuccess: async (res) => {
        const newId = res?.data?.id;
        if (newId && pendingFiles.length > 0) {
          setIsUploading(true);
          for (const { file } of pendingFiles) {
            try {
              const formData = new FormData();
              formData.append("file", file);
              const uploadRes = await fetch(`/api/change-orders/${newId}/attachments`, {
                method: "POST",
                body: formData,
              });
              if (!uploadRes.ok) {
                const errBody = await uploadRes
                  .json()
                  .catch(() => ({ error: `Upload failed: ${uploadRes.status}` }));
                throw new Error(
                  (errBody as { error?: string }).error ?? `Upload failed: ${uploadRes.status}`,
                );
              }
            } catch (err) {
              const msg = err instanceof Error ? err.message : "Upload failed";
              toast.error(`${file.name}: ${msg}`);
            }
          }
          setIsUploading(false);
        }
        resetForm();
        onOpenChange(false);
      },
    });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) resetForm();
        onOpenChange(o);
      }}
    >
      <DialogContent className="w-[50vw] max-w-[50vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Potential Change Order</DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 text-xs">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center gap-2">
              <span
                className={cn(
                  "px-2 py-1 rounded-md font-medium",
                  step === s.id
                    ? "bg-[#334155] text-white"
                    : step > s.id
                      ? "bg-gray-200 text-gray-700"
                      : "bg-gray-100 text-gray-500",
                )}
              >
                {s.label}
              </span>
              {i < STEPS.length - 1 && <span className="text-gray-300">—</span>}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {step === 1 && (
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-1.5">
                <Label htmlFor="pco-title">Title *</Label>
                <Input
                  id="pco-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Additional millwork in lobby"
                />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label htmlFor="pco-description">Description</Label>
                <Textarea
                  id="pco-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Linkage type — drives whether the PCO ties to a parent
                  commitment or to a cost code (US-125). */}
              <div className="col-span-2 space-y-2">
                <Label>Linkage Type *</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setLinkageType("commitment");
                      setCostCodeId("");
                      setSelectedSub("");
                      setFormError(null);
                    }}
                    className={cn(
                      "rounded-md border px-3 py-3 text-left text-sm",
                      linkageType === "commitment"
                        ? "border-[#334155] bg-[#334155] text-white"
                        : "border-gray-300 hover:bg-gray-50",
                    )}
                  >
                    <div className="font-medium">Existing Commitment</div>
                    <div
                      className={cn(
                        "text-xs mt-0.5",
                        linkageType === "commitment" ? "text-gray-200" : "text-gray-500",
                      )}
                    >
                      Change is in scope of an existing subcontract or PO.
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setLinkageType("cost_code");
                      setParentCommitmentId("");
                      // Default the fresh starter line to whatever cost code is
                      // already picked at the header (if any).
                      setLines([blankLine(costCodeId)]);
                      setLinesHydratedFor(null);
                      setFormError(null);
                    }}
                    className={cn(
                      "rounded-md border px-3 py-3 text-left text-sm",
                      linkageType === "cost_code"
                        ? "border-[#334155] bg-[#334155] text-white"
                        : "border-gray-300 hover:bg-gray-50",
                    )}
                  >
                    <div className="font-medium">Cost Code (Unforeseen)</div>
                    <div
                      className={cn(
                        "text-xs mt-0.5",
                        linkageType === "cost_code" ? "text-gray-200" : "text-gray-500",
                      )}
                    >
                      Hidden conditions, no parent contract. Allocate against a cost code.
                    </div>
                  </button>
                </div>
              </div>

              {linkageType === "commitment" && (
                <div className="col-span-2 space-y-1.5">
                  <Label>Linked Commitment *</Label>
                  <Select
                    value={parentCommitmentId || ""}
                    onValueChange={(v) => setParentCommitmentId(v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Pick the subcontract or PO this change belongs to" />
                    </SelectTrigger>
                    <SelectContent>
                      {parentCandidates.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.commitmentNumber ?? "--"} — {c.title ?? "--"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {parentCommitmentId && (
                    <p className="text-xs text-gray-600 pt-1">
                      Subcontractor:{" "}
                      <span className="font-medium text-gray-900">{subcontractorName ?? "—"}</span>
                    </p>
                  )}
                </div>
              )}

              {linkageType === "cost_code" && (
                <>
                  <div className="col-span-2 space-y-1.5">
                    <Label>Cost Code *</Label>
                    <Select value={costCodeId || ""} onValueChange={selectHeaderCostCode}>
                      <SelectTrigger>
                        <SelectValue placeholder="Pick the cost code this change allocates against" />
                      </SelectTrigger>
                      <SelectContent>
                        {(costCodes ?? []).map((cc) => (
                          <SelectItem key={cc.id} value={cc.id}>
                            {cc.code} — {cc.description ?? ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {costCodeId && (
                    <div className="col-span-2 space-y-1.5">
                      <Label>Subcontractor *</Label>
                      <Select value={selectedSub || ""} onValueChange={(v) => setSelectedSub(v)}>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              committedSubsQuery.isLoading
                                ? "Loading subs…"
                                : "Pick a sub already committed under this cost code"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {committedSubs.map((s) => (
                            <SelectItem key={s.companyId} value={s.companyId}>
                              {s.companyName} — {s.commitmentNumber}
                            </SelectItem>
                          ))}
                          {committedSubs.length > 0 && (
                            <div className="my-1 border-t border-gray-200" />
                          )}
                          <SelectItem value="__new_vendor__">
                            + New Vendor (requires buyout)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      {committedSubs.length === 0 && !committedSubsQuery.isLoading && (
                        <p className="text-xs text-gray-500 pt-1">
                          No subs are currently committed under this cost code. Pick &quot;New
                          Vendor&quot; to flag this PCO for buyout.
                        </p>
                      )}
                      {isNewVendor && (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mt-1">
                          This PCO can be created now, but cannot be approved until buyout completes
                          for this cost code. It will surface in the Buy Log.
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Line Items</Label>
                  {parentCommitmentId && (
                    <p className="text-xs text-gray-500 pt-0.5">
                      Pre-filled from{" "}
                      <span className="font-medium text-gray-700">
                        {selectedParent?.commitmentNumber ?? ""}
                      </span>
                      . Enter delta amounts per line (positive or negative).
                    </p>
                  )}
                  {linkageType === "cost_code" && selectedCostCode && (
                    <p className="text-xs text-gray-500 pt-0.5">
                      Lines default to{" "}
                      <span className="font-medium text-gray-700">{selectedCostCode.code}</span> —
                      change a line&apos;s cost code if it lands somewhere different.
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {parentCommitmentId && parentLineItems && parentLineItems.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={resetLinesFromParent}
                    >
                      <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                      Reset to parent
                    </Button>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={addLine}>
                    <Plus className="h-3.5 w-3.5 mr-1.5" />
                    Add Line
                  </Button>
                </div>
              </div>

              {parentCommitmentId && parentDetailQuery.isLoading && !parentLineItems && (
                <p className="text-xs text-gray-500">Loading parent line items…</p>
              )}

              <div className="rounded-md border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-left">
                      <th className="px-3 py-2 font-medium text-gray-600 w-[40%]">Description</th>
                      <th className="px-3 py-2 font-medium text-gray-600 w-[30%]">Cost Code</th>
                      <th className="px-3 py-2 font-medium text-gray-600 text-right w-[20%]">
                        Amount
                      </th>
                      <th className="w-[10%]" />
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line, i) => (
                      <tr key={line.key} className="border-t">
                        <td className="px-3 py-2">
                          <Input
                            value={line.description}
                            onChange={(e) => updateLine(i, { description: e.target.value })}
                            placeholder="Scope of line"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Select
                            value={line.costCodeId || "none"}
                            onValueChange={(v) =>
                              updateLine(i, { costCodeId: v === "none" ? "" : v })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select cost code" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No cost code</SelectItem>
                              {(costCodes ?? []).map((cc) => (
                                <SelectItem key={cc.id} value={cc.id}>
                                  {cc.code} — {cc.description ?? ""}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            step="0.01"
                            className="text-right"
                            value={line.scheduledValue}
                            onChange={(e) => updateLine(i, { scheduledValue: e.target.value })}
                            placeholder="0.00"
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            disabled={lines.length === 1}
                            onClick={() => removeLine(i)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t">
                    <tr>
                      <td colSpan={2} className="px-3 py-2 text-right font-medium text-gray-600">
                        Total ({direction})
                      </td>
                      <td className="px-3 py-2 text-right font-semibold">
                        {formatCurrency(total)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="pco-schedule-impact">Schedule Impact (days)</Label>
                <Input
                  id="pco-schedule-impact"
                  type="number"
                  value={scheduleImpactDays}
                  onChange={(e) => setScheduleImpactDays(e.target.value)}
                  placeholder="0"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pco-deadline">Client Decision Deadline</Label>
                <DatePicker
                  value={clientDecisionDeadline || null}
                  onChange={(next) => setClientDecisionDeadline(next ?? "")}
                  ariaLabel="Client decision deadline"
                  className="w-full"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pco-ret-override">Retention Override (%)</Label>
                <Input
                  id="pco-ret-override"
                  type="number"
                  step="0.01"
                  value={retentionOverride}
                  onChange={(e) => setRetentionOverride(e.target.value)}
                  placeholder="Inherit parent"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Change Type</Label>
                <div className="h-10 px-3 rounded-md border bg-gray-50 flex items-center text-sm text-gray-600">
                  {direction} · {formatCurrency(total)}
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <div>
                <Label>Supporting Documents</Label>
                <p className="text-xs text-gray-500 pt-0.5">
                  Attach the quote, sketch, email, or authorization that justifies this PCO.
                  Reviewers will see these with the change order request.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  multiple
                  onChange={handleFilesSelected}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-3.5 w-3.5 mr-1.5" />
                  Add file
                </Button>
                <span className="text-xs text-gray-500">
                  PDF, Word, Excel, or images · up to 50 MB each
                </span>
              </div>

              {pendingFiles.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No attachments staged.</p>
              ) : (
                <div className="space-y-2">
                  {pendingFiles.map((entry) => (
                    <div
                      key={entry.key}
                      className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm"
                    >
                      <Paperclip className="h-4 w-4 shrink-0 text-gray-500" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{entry.file.name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(entry.file.size)}</p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-gray-500 hover:text-red-600"
                        onClick={() => removePendingFile(entry.key)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="rounded-md border bg-gray-50 p-3 text-sm text-gray-700">
                <div className="font-medium text-gray-900">Review</div>
                <div className="text-xs text-gray-600 mt-1">Title: {title || "—"}</div>
                <div className="text-xs text-gray-600">
                  Linkage:{" "}
                  {linkageType === "commitment" ? "Existing Commitment" : "Cost Code (Unforeseen)"}
                </div>
                {linkageType === "commitment" ? (
                  <div className="text-xs text-gray-600">
                    Linked Commitment:{" "}
                    {selectedParent
                      ? `${selectedParent.commitmentNumber ?? ""} — ${selectedParent.title ?? ""}`
                      : "—"}
                  </div>
                ) : (
                  <div className="text-xs text-gray-600">
                    Cost Code:{" "}
                    {selectedCostCode
                      ? `${selectedCostCode.code} — ${selectedCostCode.description ?? ""}`
                      : "—"}
                  </div>
                )}
                <div className="text-xs text-gray-600">
                  Subcontractor: {subcontractorName ?? "—"}
                </div>
                <div className="text-xs text-gray-600">Lines: {lines.length}</div>
                <div className="text-xs text-gray-600">
                  Total: {formatCurrency(total)} ({direction})
                </div>
                <div className="text-xs text-gray-600">
                  Attachments: {pendingFiles.length} file
                  {pendingFiles.length === 1 ? "" : "s"}
                </div>
              </div>
            </div>
          )}

          {formError && <p className="text-sm text-red-600">{formError}</p>}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {step > 1 && (
              <Button type="button" variant="outline" onClick={goBack}>
                Back
              </Button>
            )}
            {step < 4 ? (
              <Button
                type="button"
                onClick={goNext}
                className="bg-[#334155] hover:bg-[#415A77] text-white"
              >
                Next
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={create.isPending || isUploading}
                className="bg-[#334155] hover:bg-[#415A77] text-white"
              >
                {create.isPending ? "Saving…" : isUploading ? "Uploading files…" : "Create PCO"}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
